import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CircleNotch, EnvelopeSimple, ArrowLeft } from "@phosphor-icons/react";

const LOGO = "/novuspak-logo.png";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[#F8F9FA]">
      <div className="w-full max-w-md animate-fade-up" data-testid="forgot-password-page">
        <div className="flex items-center gap-3 mb-8">
          <img src={LOGO} alt="NovusPak" className="h-12 w-12 object-contain shrink-0" />
          <span className="font-heading font-extrabold text-xl text-[#0F172A] leading-tight">Control de Inventario</span>
        </div>

        {sent ? (
          <div data-testid="forgot-success" className="bg-white border border-[#E2E8F0] rounded-md p-6">
            <div className="w-12 h-12 rounded-full bg-[#ECFDF5] flex items-center justify-center mb-4">
              <EnvelopeSimple size={24} weight="bold" className="text-[#065F46]" />
            </div>
            <h1 className="font-heading text-2xl font-black text-[#0F172A]">Revisa tu correo</h1>
            <p className="text-[#475569] mt-2">
              Si <strong>{email}</strong> está registrado, te enviamos un enlace para restablecer tu contraseña.
              El enlace expira en 30 minutos.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 mt-6 font-semibold text-[#0F172A] underline underline-offset-4">
              <ArrowLeft size={16} weight="bold" /> Volver a iniciar sesión
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight text-[#0F172A]">Recuperar contraseña</h1>
            <p className="text-[#475569] mt-2 mb-8">Ingresa tu email y te enviaremos un enlace para crear una nueva contraseña.</p>

            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.15em] text-[#64748B]">Email</Label>
                <Input
                  id="email"
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tucorreo@empresa.com"
                  className="h-11 bg-white border-[#E2E8F0] focus-visible:ring-[#0F172A]"
                />
              </div>

              {error && (
                <p data-testid="forgot-error" className="text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2">{error}</p>
              )}

              <Button type="submit" data-testid="forgot-submit" disabled={loading} className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold">
                {loading ? <CircleNotch size={18} className="animate-spin" /> : "Enviar enlace"}
              </Button>
            </form>

            <Link to="/login" data-testid="back-to-login" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-[#475569] hover:text-[#0F172A]">
              <ArrowLeft size={16} weight="bold" /> Volver a iniciar sesión
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
